import React, { useCallback, useRef, useState } from 'react';
import FileDropZone from '../shared/FileDropZone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCloudArrowUp, faDownload, faExclamationTriangle, faFileLines,
    faInfoCircle, faSpinner, faTimes, faCheckCircle, faBan, faUpload,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import '../Invoicing/CSVUploadModal.css';
import './CRMCreditBulkUploadModal.css';

/**
 * Import Partners — preview, then import.
 *
 * Same two steps as Update Credit Terms: the preview call writes nothing, and
 * the user presses Import on the row-by-row plan they just read (new partner /
 * already in HMS / could not be imported, with the reason). Replaces the shared
 * CSVUploadModal here, which posted and created in one go.
 */

const ENDPOINT = '/api/crm/profiles/bulk-upload/';

export const PARTNER_IMPORT_COLUMNS = [
    'Partner Name', 'Partner Type', 'Partner Number',
    'Address Line 1', 'Address Line 2', 'City', 'Country', 'Postal Code',
    'Bank Name', 'Bank Account Number', 'IBAN', 'SWIFT Code',
    'Credit Limit', 'Credit Days', 'Risk Rating',
];

const PARTNER_IMPORT_EXAMPLE = [
    'Acme Shipping Ltd', 'Debtor', '4200',
    '123 Harbour Road', 'Suite 4', 'Cape Town', 'South Africa', '8001',
    'Standard Bank', '12345678', 'ZA12345600001234567890', 'SBZAZAJJ',
    '500000', '30', 'Low',
];

const OUTCOME_META = {
    create: { label: 'New partner', className: 'ccb-badge-apply', icon: faCheckCircle },
    existing: { label: 'Already in IMT', className: 'ccb-badge-neutral', icon: faInfoCircle },
    refused: { label: 'Not imported', className: 'ccb-badge-blocked', icon: faBan },
};

const CRMPartnerImportModal = ({ isOpen, onClose, onImported }) => {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const reset = useCallback(() => {
        setFile(null);
        setPreview(null);
        setResult(null);
        setError(null);
        setBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    const selectFile = (chosen) => {
        if (!chosen) return;
        if (!chosen.name.toLowerCase().endsWith('.csv')) {
            setError('Please select a CSV file.');
            return;
        }
        setFile(chosen);
        setPreview(null);
        setResult(null);
        setError(null);
    };

    const post = async (isPreview) => {
        const payload = new FormData();
        payload.append('file', file);
        if (isPreview) payload.append('preview', 'true');
        setBusy(true);
        setError(null);
        try {
            const response = await axiosInstance.post(ENDPOINT, payload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data;
        } catch (err) {
            const data = err.response?.data;
            // A file with nothing new still carries its plan: show it, so the
            // user sees WHY nothing could be imported rather than a bare failure.
            if (data?.plan) return data;
            setError(data?.error || 'Upload failed. Please check the file and try again.');
            return null;
        } finally {
            setBusy(false);
        }
    };

    const handlePreview = async () => {
        const data = await post(true);
        if (data) setPreview(data);
    };

    const handleImport = async () => {
        const data = await post(false);
        if (!data) return;
        setResult(data);
        setPreview(null);
        if (onImported) onImported(data);
    };

    const handleDownloadTemplate = () => {
        const lines = [PARTNER_IMPORT_COLUMNS.join(';'), PARTNER_IMPORT_EXAMPLE.join(';')];
        const blob = new Blob(['﻿' + lines.join('\n') + '\n'], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'HMS_Import_Partners_Template.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    const shown = preview || result;
    const summary = shown?.summary;
    const plan = shown?.plan || [];
    // Rows that need the reader's eye first: refusals, then new, then existing.
    const order = { refused: 0, create: 1, existing: 2 };
    const sortedPlan = [...plan].sort((a, b) => (order[a.outcome] - order[b.outcome]) || (a.row - b.row));

    return (
        <div className="csv-modal-overlay" onClick={handleClose}>
            <div className="csv-modal ccb-modal" onClick={(e) => e.stopPropagation()}>

                <div className="csv-modal-header">
                    <div className="csv-modal-header-text">
                        <FontAwesomeIcon icon={faCloudArrowUp} className="csv-modal-header-icon" />
                        <h2>Import Partners</h2>
                    </div>
                    <button className="csv-modal-close" onClick={handleClose} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="csv-modal-body">

                    {!shown && (
                        <>
                            <div className="csv-template-section">
                                <div className="csv-template-header">
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                    <span>What this does</span>
                                </div>
                                <p className="csv-template-desc">
                                    Adds new partners. Partners IMT already holds are listed and left
                                    unchanged — use Update Credit Terms to change their credit.
                                </p>
                                <div className="csv-template-columns">
                                    {PARTNER_IMPORT_COLUMNS.map(col => (
                                        <span key={col} className="csv-column-tag">{col}</span>
                                    ))}
                                </div>
                                <ul className="csv-template-notes">
                                    <li>Partner Name and Partner Type (Debtor, Creditor or Both) are required.</li>
                                    <li>Partner Number is optional; a column headed Account Number is read as the Partner Number.</li>
                                    <li>Credit Limit may be written 500000, 500,000 or $500.000.</li>
                                    <li>Credit Days is a whole number, or CIA for cash in advance.</li>
                                    <li>Risk Rating is Zero, Low, Medium (or Med) or High.</li>
                                    <li>You see every row before anything is saved. New partners start as Pending.</li>
                                </ul>
                                <button type="button" className="csv-download-template-btn" onClick={handleDownloadTemplate}>
                                    <FontAwesomeIcon icon={faDownload} /> Download Template CSV
                                </button>
                            </div>

                            <div className="csv-form-group">
                                <label className="csv-form-label">Upload File</label>
                                <FileDropZone
                                    className={`csv-dropzone ${file ? 'has-file' : ''}`}
                                    activeClassName="drag-over"
                                    onFiles={(files) => selectFile(files[0])}
                                    onError={setError}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv"
                                        onChange={(e) => selectFile(e.target.files[0])}
                                        hidden
                                    />
                                    {file ? (
                                        <div className="csv-file-info">
                                            <FontAwesomeIcon icon={faFileLines} className="csv-file-icon" />
                                            <div className="csv-file-details">
                                                <span className="csv-file-name">{file.name}</span>
                                                <span className="csv-file-size">{(file.size / 1024).toFixed(1)} KB</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="csv-dropzone-content">
                                            <FontAwesomeIcon icon={faCloudArrowUp} className="csv-dropzone-icon" />
                                            <span className="csv-dropzone-text">Drag &amp; drop your file here</span>
                                            <span className="csv-dropzone-hint">or click to browse — CSV only</span>
                                        </div>
                                    )}
                                </FileDropZone>
                            </div>
                        </>
                    )}

                    {error && (
                        <div className="csv-result-banner csv-result-error">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span>{error}</span>
                        </div>
                    )}

                    {shown && (
                        <div className={`csv-result-banner ${result && result.created > 0 ? 'csv-result-success' : 'ccb-banner-neutral'}`}>
                            <FontAwesomeIcon icon={result && result.created > 0 ? faCheckCircle : faInfoCircle} />
                            <span>{shown.message}</span>
                        </div>
                    )}

                    {summary && (
                        <div className="ccb-summary" data-testid="partner-import-summary">
                            <div className="ccb-stat">
                                <span className="ccb-stat-value">{summary.create}</span>
                                <span className="ccb-stat-label">{result ? 'partners imported' : 'new partners'}</span>
                            </div>
                            <div className="ccb-stat ccb-stat-muted">
                                <span className="ccb-stat-value">{summary.existing}</span>
                                <span className="ccb-stat-label">already in IMT</span>
                            </div>
                            <div className="ccb-stat ccb-stat-approval">
                                <span className="ccb-stat-value">{summary.refused}</span>
                                <span className="ccb-stat-label">not imported</span>
                            </div>
                        </div>
                    )}

                    {sortedPlan.length > 0 && (
                        <div className="ccb-plan">
                            <div className="ccb-plan-header">
                                {preview ? 'Review before importing' : 'What happened'}
                            </div>
                            <div className="ccb-plan-list">
                                {sortedPlan.map((entry) => {
                                    const meta = OUTCOME_META[entry.outcome] || OUTCOME_META.refused;
                                    return (
                                        <div key={entry.row} className="ccb-plan-row" data-outcome={entry.outcome}>
                                            <div className="ccb-plan-row-head">
                                                <span className="ccb-plan-rownum">Row {entry.row}</span>
                                                <span className="ccb-plan-name">
                                                    {entry.name}
                                                    {entry.partner_number ? ` · ${entry.partner_number}` : ''}
                                                </span>
                                                <span className={`ccb-badge ${meta.className}`}>
                                                    <FontAwesomeIcon icon={meta.icon} /> {meta.label}
                                                </span>
                                            </div>
                                            {entry.outcome !== 'create' && (
                                                <div className="ccb-plan-note">{entry.message}</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="csv-modal-footer">
                    {result ? (
                        <button type="button" className="csv-btn-upload" onClick={handleClose}>Done</button>
                    ) : preview ? (
                        <>
                            <button type="button" className="csv-btn-cancel" onClick={reset} disabled={busy}>
                                Choose another file
                            </button>
                            <button
                                type="button"
                                className="csv-btn-upload"
                                onClick={handleImport}
                                disabled={busy || !summary?.create}
                            >
                                {busy
                                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Importing...</>
                                    : <><FontAwesomeIcon icon={faCheckCircle} /> Import {summary?.create || 0} partner(s)</>}
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="csv-btn-cancel" onClick={handleClose}>Cancel</button>
                            <button
                                type="button"
                                className="csv-btn-upload"
                                onClick={handlePreview}
                                disabled={!file || busy}
                            >
                                {busy
                                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Checking...</>
                                    : <><FontAwesomeIcon icon={faUpload} /> Preview import</>}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CRMPartnerImportModal;
